/**
 * NfseReadinessGate — tela do gerador quando a empresa ainda não está pronta para
 * emitir NFS-e. Substitui o antigo FiscalOnboardingWizard: em vez de um wizard
 * separado, lista o que falta (via readiness) e leva o usuário ao cadastro da
 * empresa, onde regime/CNPJ/município/certificado são configurados num só lugar.
 */
import { Card, CardContent } from '@/components/ui/card';
import { Button3D } from '@/components/ui/button-3d';
import { Building2, AlertCircle, WifiOff } from 'lucide-react';
import type { FiscalMissing } from '@/hooks/useFiscalReadiness';

export function NfseReadinessGate({
  missing,
  onConfigure,
  offline = false,
}: {
  missing: FiscalMissing[];
  onConfigure: () => void;
  /** Sem internet: configurar exige rede (updateCompany é online-only). */
  offline?: boolean;
}) {
  return (
    <Card>
      <CardContent className="py-8">
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Building2 className="h-7 w-7 text-primary" />
          </div>
          <h2 className="text-lg font-semibold">Configure sua empresa para emitir</h2>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Falta pouco. Complete os dados fiscais no cadastro da empresa e você já
            poderá emitir sua NFS-e.
          </p>
        </div>

        {missing.length > 0 && (
          <ul className="mx-auto mt-6 max-w-sm space-y-2">
            {missing.map((m) => (
              <li key={m.key} className="flex items-start gap-2 text-sm">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
                <span className="text-muted-foreground">{m.label}</span>
              </li>
            ))}
          </ul>
        )}

        <Button3D className="mt-6 w-full" onClick={onConfigure} disabled={offline}>
          <Building2 className="mr-2 h-4 w-4" />
          Configurar empresa
        </Button3D>
        {offline && (
          <p className="mt-2 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <WifiOff className="h-3.5 w-3.5" aria-hidden />
            Conecte-se à internet para configurar.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
